import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '../lib/cn';
import styles from './Toast.module.css';

const listeners = new Set();
let nextId = 1;

function emit(tone, title, options = {}) {
  const item = { id: nextId++, tone, title, ...options };
  listeners.forEach((listener) => listener(item));
  return item.id;
}

/** toast.success('Saved') · toast.error('Nope', { description }) — callable from anywhere. */
export const toast = {
  success: (title, options) => emit('success', title, options),
  error: (title, options) => emit('error', title, { duration: 7000, ...options }),
  info: (title, options) => emit('info', title, options),
  warning: (title, options) => emit('warning', title, options),
};

const ICONS = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

function ToastItem({ item, onDismiss }) {
  const Icon = ICONS[item.tone] || Info;
  const timer = useRef(null);

  const start = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onDismiss(item.id), item.duration || 5000);
  }, [item.id, item.duration, onDismiss]);

  useEffect(() => {
    start();
    return () => clearTimeout(timer.current);
  }, [start]);

  return (
    <div
      className={cn(styles.toast, styles[item.tone])}
      role={item.tone === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => clearTimeout(timer.current)}
      onMouseLeave={start}
    >
      <Icon className={styles.icon} aria-hidden="true" />
      <div className={styles.text}>
        <p className={styles.title}>{item.title}</p>
        {item.description && <p className={styles.description}>{item.description}</p>}
      </div>
      <button type="button" className={styles.close} aria-label="Dismiss" onClick={() => onDismiss(item.id)}>
        <X />
      </button>
    </div>
  );
}

export function Toaster() {
  const [items, setItems] = useState([]);

  const dismiss = useCallback((id) => setItems((list) => list.filter((t) => t.id !== id)), []);

  useEffect(() => {
    const listener = (item) => setItems((list) => [...list.slice(-3), item]);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className={styles.region} aria-live="polite">
      {items.map((item) => (
        <ToastItem key={item.id} item={item} onDismiss={dismiss} />
      ))}
    </div>,
    document.body
  );
}
